import React, { useState, useEffect, useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api, { getAttachments, uploadAttachment, deleteAttachment, downloadAttachment } from '../services/api';
import toast from 'react-hot-toast';

function TransactionModal({ isOpen, onClose, onSave, editingTransaction, defaultType = 'EXPENSE' }) {
  const [formState, setFormState] = useState({
    title: '', amount: '', type: 'EXPENSE', category_id: '',
    payment_method: 'account', credit_card_id: '', installments: 1,
    income_type: '', withheld_ir: '', inss_deducted: '',
  });
  const [formDate, setFormDate] = useState(new Date());
  const [isPickerOpen, setIsFormPickerOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableCards, setAvailableCards] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [catRes, cardRes] = await Promise.all([api.get('/categories/'), api.get('/credit-cards/')]);
          setAvailableCategories(catRes.data); setAvailableCards(cardRes.data);
        } catch (error) {
          toast.error("Não foi possível carregar listas.");
        }
      };
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && editingTransaction?.id) {
      getAttachments(editingTransaction.id)
        .then(setAttachments)
        .catch(() => setAttachments([]));
    } else {
      setAttachments([]);
    }
  }, [isOpen, editingTransaction]);

  useEffect(() => {
    if (editingTransaction && isOpen) {
      setFormState({
        title: editingTransaction.title, amount: editingTransaction.amount, type: editingTransaction.type,
        category_id: editingTransaction.category?.id?.toString() || '',
        payment_method: editingTransaction.credit_card_id ? 'credit_card' : 'account',
        credit_card_id: editingTransaction.credit_card_id?.toString() || '',
        installments: 1, income_type: editingTransaction.income_type || '',
        withheld_ir: editingTransaction.withheld_ir || '', inss_deducted: editingTransaction.inss_deducted || '',
      });
      setFormDate(new Date(editingTransaction.date.replace(/-/g, '/')));
    } else {
      setFormState({ title: '', amount: '', type: defaultType, category_id: '', payment_method: 'account', credit_card_id: '', installments: 1, income_type: '', withheld_ir: '', inss_deducted: '' });
      setFormDate(new Date());
    }
  }, [editingTransaction, isOpen, defaultType]);

  if (!isOpen) return null;

  const handleFormChange = (e) => setFormState({ ...formState, [e.target.name]: e.target.value });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const newAttachment = await uploadAttachment(editingTransaction.id, file);
      setAttachments(prev => [...prev, newAttachment]);
      toast.success('Comprovante anexado!');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Erro ao enviar arquivo. Verifique o tipo e tamanho (máx. 10MB).';
      toast.error(msg);
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteAttachment(editingTransaction.id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Anexo removido.');
    } catch {
      toast.error('Erro ao remover anexo.');
    }
  };

  const handleDownload = async (attachment) => {
    try {
      await downloadAttachment(editingTransaction.id, attachment.id);
    } catch {
      toast.error('Erro ao baixar arquivo.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formState.title || !formState.amount || !formDate || !formState.category_id) {
      toast.error("Preencha os campos obrigatórios."); return;
    }
    const transactionData = {
      ...formState,
      amount: parseFloat(formState.amount),
      date: format(formDate, 'y-MM-dd'),
      category_id: parseInt(formState.category_id, 10),
      installments: Number(formState.installments),
      credit_card_id: formState.payment_method === 'credit_card' ? parseInt(formState.credit_card_id, 10) : null,
      income_type: formState.type === 'INCOME' && formState.income_type ? formState.income_type : null,
      withheld_ir: formState.type === 'INCOME' && formState.withheld_ir ? parseFloat(formState.withheld_ir) : null,
      inss_deducted: formState.type === 'INCOME' && formState.inss_deducted ? parseFloat(formState.inss_deducted) : null,
    };
    onSave(transactionData, editingTransaction?.id || null);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 font-inter" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h3 className="text-xl font-bold font-manrope text-slate-900">
            {editingTransaction ? 'Editar Registro' : 'Novo Lançamento'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-5">
             <div>
               <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Título / Descrição</label>
               <input name="title" type="text" value={formState.title} onChange={handleFormChange} placeholder="Ex: Aluguel, Salário, Compra..." required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-[#4f46e5] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#4f46e5]" />
             </div>

             <div className="grid grid-cols-2 gap-4">
               <div>
                 <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Valor (R$)</label>
                 <input name="amount" type="number" step="0.01" value={formState.amount} onChange={handleFormChange} placeholder="0.00" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-[#4f46e5] font-bold text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#4f46e5]" />
               </div>
               <div>
                 <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Tipo</label>
                 <select name="type" value={formState.type} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-[#4f46e5] text-slate-900 font-bold focus:outline-none focus:border-[#4f46e5]">
                   <option value="EXPENSE">Despesa</option>
                   <option value="INCOME">Receita</option>
                 </select>
               </div>
             </div>

             <div>
               <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Categoria</label>
               <select name="category_id" value={formState.category_id} onChange={handleFormChange} required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-[#4f46e5] text-slate-900 focus:outline-none focus:border-[#4f46e5]">
                 <option value="" disabled>Selecione...</option>
                 {availableCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
               </select>
             </div>

             <div className="relative">
               <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Data do Lançamento</label>
               <button type="button" onClick={() => setIsFormPickerOpen(!isPickerOpen)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-[#4f46e5] text-slate-900 text-left flex items-center justify-between hover:bg-slate-100 transition-colors">
                  <span>{format(formDate, 'dd/MM/yyyy', { locale: ptBR })}</span>
                  <span className="material-symbols-outlined text-slate-400 text-[18px]">calendar_today</span>
               </button>
               {isPickerOpen && (
                 <div className="absolute top-full left-0 mt-2 bg-white shadow-xl rounded-xl z-50 p-2 border border-slate-100">
                   <DayPicker mode="single" selected={formDate} onSelect={(date) => { if(date) setFormDate(date); setIsFormPickerOpen(false); }} locale={ptBR} initialFocus />
                 </div>
               )}
             </div>

             {/* Dynamic Sub-forms */}
             {formState.type === 'EXPENSE' && (
               <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
                 <div>
                   <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Meio de Pagamento</label>
                   <select name="payment_method" value={formState.payment_method} onChange={handleFormChange} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm">
                     <option value="account">Conta Pessoal / PIX</option>
                     <option value="credit_card">Cartão de Crédito</option>
                   </select>
                 </div>
                 {formState.payment_method === 'credit_card' && (
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Qual Cartão?</label>
                       <select name="credit_card_id" value={formState.credit_card_id} onChange={handleFormChange} required className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm">
                         <option value="" disabled>Selecione...</option>
                         {availableCards.map(card => <option key={card.id} value={card.id}>{card.name}</option>)}
                       </select>
                     </div>
                     {!editingTransaction && (
                       <div>
                         <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Parcelas</label>
                         <input name="installments" type="number" min="1" max="48" value={formState.installments} onChange={handleFormChange} required className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm" />
                       </div>
                     )}
                   </div>
                 )}
               </div>
             )}

             {formState.type === 'INCOME' && (
               <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Informações Fiscais</p>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Tipo de Receita</label>
                    <select name="income_type" value={formState.income_type} onChange={handleFormChange} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm">
                      <option value="">Não classificar base</option>
                      <option value="SALARY_CLT">Salário CLT</option>
                      <option value="SALARY_PJ">Pró-labore / PJ</option>
                      <option value="RENTAL">Aluguel</option>
                      <option value="INVESTMENT">Rendimento Isento</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">IRRF Retido</label>
                      <input name="withheld_ir" type="number" step="0.01" min="0" value={formState.withheld_ir} onChange={handleFormChange} placeholder="0.00" className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">INSS Retido</label>
                      <input name="inss_deducted" type="number" step="0.01" min="0" value={formState.inss_deducted} onChange={handleFormChange} placeholder="0.00" className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm" />
                    </div>
                  </div>
               </div>
             )}

             {editingTransaction && (
               <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                 <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                   <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Comprovantes</p>
                   <button
                     type="button"
                     onClick={() => fileInputRef.current?.click()}
                     disabled={isUploading}
                     className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50 transition-colors"
                   >
                     <span className="material-symbols-outlined text-[16px]">attach_file</span>
                     {isUploading ? 'Enviando...' : 'Anexar arquivo'}
                   </button>
                   <input
                     ref={fileInputRef}
                     type="file"
                     accept=".png,.jpg,.jpeg,.gif,.pdf,.webp"
                     className="hidden"
                     onChange={handleFileUpload}
                   />
                 </div>

                 {attachments.length === 0 ? (
                   <p className="text-xs text-slate-400 text-center py-2">Nenhum comprovante anexado.</p>
                 ) : (
                   <ul className="space-y-2">
                     {attachments.map(att => (
                       <li key={att.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2">
                         <div className="flex items-center gap-2 min-w-0">
                           <span className="material-symbols-outlined text-slate-400 text-[18px] shrink-0">
                             {att.mime_type === 'application/pdf' ? 'picture_as_pdf' : 'image'}
                           </span>
                           <div className="min-w-0">
                             <p className="text-xs font-medium text-slate-700 truncate max-w-[180px]">{att.original_filename}</p>
                             <p className="text-[10px] text-slate-400">{formatFileSize(att.file_size)}</p>
                           </div>
                         </div>
                         <div className="flex items-center gap-1 shrink-0">
                           <button type="button" onClick={() => handleDownload(att)} title="Baixar" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors">
                             <span className="material-symbols-outlined text-[16px]">download</span>
                           </button>
                           <button type="button" onClick={() => handleDeleteAttachment(att.id)} title="Remover" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                             <span className="material-symbols-outlined text-[16px]">delete</span>
                           </button>
                         </div>
                       </li>
                     ))}
                   </ul>
                 )}
               </div>
             )}

             <button type="submit" className="w-full bg-[#4f46e5] text-white py-3.5 px-4 rounded-xl font-bold font-manrope shadow-md shadow-indigo-600/30 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all mt-4">
               {editingTransaction ? 'Salvar Edição' : 'Concluir Lançamento'}
             </button>
          </form>
        </div>

      </div>
    </div>
  );
}

export default TransactionModal;